import { useQuery } from '@tanstack/react-query';
import {
  Flex,
  FormattedDateTime,
  Link,
  Panel,
  PanelHeader,
  PanelInset,
  Paragraph,
  Spinner,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { customerDisplay } from '@/services/db/customer/display';
import { getRelatedCustomersQueryOptions } from '@/services/db/object/queries';
import { generatePath } from '@/services/router/generate-path';
import { useObjectDetail } from './context';
import { relationshipCustomer, relationshipKey } from './relationships/relationship-customer';

const PREVIEW_PAGE_SIZE = 5;

export function ObjectRelationshipsPreview() {
  const { record, workspaceId, hrefs } = useObjectDetail();
  const query = useQuery(
    getRelatedCustomersQueryOptions(workspaceId, record.identifiers.cio_object_id, { size: PREVIEW_PAGE_SIZE }),
  );

  const total = query.data?.meta.pagination.total ?? 0;
  const relationships = query.data?.relationships ?? [];
  const customersByCioId = new Map((query.data?.customers ?? []).map((customer) => [customer.internal_id, customer]));
  const personSingular = 'Person';

  return (
    <Panel data-test-relationships-preview>
      <PanelHeader>
        <Flex w="full" justifyContent="space-between" alignItems="center">
          <Text weight="bold">
            {query.isSuccess ? `${total.toLocaleString()} ${plur(personSingular, total)}` : 'Relationships'}
          </Text>
          <Link
            href={hrefs.tabs.relationships}
            variant="primary"
            isUnderlined={false}
            icon="chevron-right"
            iconPosition="trailing"
          >
            More
          </Link>
        </Flex>
      </PanelHeader>

      {query.isPending ? (
        <Flex justifyContent="center" alignItems="center" py="200">
          <Spinner shouldShowLoadingText label="Fetching relationships…" />
        </Flex>
      ) : query.isError ? (
        <PanelInset pb="200">
          <Paragraph variant="secondary">Could not load relationships. Try again later.</Paragraph>
        </PanelInset>
      ) : (
        <PanelInset pb="200">
          <Table caption="List of relationships" layout="fixed">
            <TableThead>
              <TableTr>
                <TableTh>{personSingular}</TableTh>
                <TableTh data-test-attributes-column>Relationship attributes</TableTh>
                <TableTh textAlign="right">created_at</TableTh>
              </TableTr>
            </TableThead>
            <TableTbody>
              {relationships.length === 0 ? (
                <TableTr>
                  <TableTd colSpan={3}>
                    <Paragraph textAlign="center" variant="secondary">
                      No relationships yet.
                    </Paragraph>
                  </TableTd>
                </TableTr>
              ) : (
                relationships.map((relationship) => {
                  const customer = relationshipCustomer(
                    relationship,
                    record.identifiers.cio_object_id,
                    customersByCioId,
                  );
                  const attributeCount = Object.keys(relationship.attributes).length;
                  return (
                    <TableTr key={relationshipKey(relationship)} data-test-relationships-table-entry>
                      <TableTd>
                        {customer == null ? (
                          <Text color="subtle">—</Text>
                        ) : (
                          <Truncated>
                            <Link
                              // The person profile is still served by the embedded Ember app.
                              href={generatePath('/workspaces/:workspaceId/journeys/people/:personId', {
                                workspaceId,
                                personId: customer.internal_id,
                              })}
                              variant="primary"
                              className="fs-mask"
                            >
                              {customerDisplay(customer, personSingular)}
                            </Link>
                          </Truncated>
                        )}
                      </TableTd>
                      <TableTd className="fs-mask">
                        {attributeCount.toLocaleString()} {plur('attribute', attributeCount)}
                      </TableTd>
                      <TableTd textAlign="right">
                        <FormattedDateTime date={relationship.created_at} format="medium" size="sm" />
                      </TableTd>
                    </TableTr>
                  );
                })
              )}
            </TableTbody>
          </Table>
        </PanelInset>
      )}
    </Panel>
  );
}
